import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowRight, ShoppingBag } from "lucide-react";
import { fetchProducts } from "../../lib/api";
import { useCart } from "../../context/CartContext";
import { RatingLine } from "../../components/Rating";
import { formatINR } from "../../lib/constants";

const COLS = {
    2: "grid-cols-1 sm:grid-cols-2",
    3: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
    4: "grid-cols-1 sm:grid-cols-2 xl:grid-cols-4",
};

const ConcernCard = ({ p, accent, detailsLabel, cartLabel, onAdd }) => {
    const [broken, setBroken] = useState(false);
    return (
    <div className="group flex flex-col rounded-2xl bg-white border border-black/[0.06] p-4 shadow-[0_1px_0_rgba(0,0,0,0.04)]
        hover:shadow-[0_18px_40px_-24px_rgba(0,0,0,0.45)] hover:-translate-y-1 transition-[transform,box-shadow] duration-300"
        data-testid={`concern-product-${p.slug}`}>
        <Link to={`/product/${p.slug}`} className="block" data-testid={`concern-product-link-${p.slug}`}>
            <div className="aspect-[4/5] rounded-xl bg-[#F1F1EC] overflow-hidden flex items-center justify-center">
                {p.images?.[0] && !broken ? (
                    <img src={p.images[0]} alt={p.name} loading="lazy" onError={() => setBroken(true)}
                        className="w-full h-full object-contain p-4 group-hover:scale-[1.04] transition-transform duration-500" />
                ) : (
                    <span className="font-display text-2xl uppercase tracking-tight text-black/25 px-4 text-center">
                        {p.name}
                    </span>
                )}
            </div>
            <h3 className="mt-4 font-display text-xl uppercase tracking-tight text-[#141414]">{p.name}</h3>
            <p className="mt-1 text-sm text-black/55 leading-snug line-clamp-2">
                {p.tagline || p.short_description || ""}
            </p>
            <div className="mt-3 flex items-center justify-between gap-3">
                <RatingLine product={p} textClass="text-xs text-black/60" />
                {p.price > 0 && (
                    <span className="font-display text-lg text-[#141414]">{formatINR(p.price)}</span>
                )}
            </div>
        </Link>
        <div className="mt-4 grid grid-cols-2 gap-2">
            <Link to={`/product/${p.slug}`} data-testid={`concern-details-${p.slug}`}
                className="inline-flex items-center justify-center gap-1.5 rounded-full border-2 px-2 py-2.5 whitespace-nowrap
                    text-[10px] uppercase tracking-[0.12em] text-[#141414] hover:bg-black/[0.04] transition-colors"
                style={{ borderColor: accent }}>
                {detailsLabel} <ArrowRight className="w-3.5 h-3.5" />
            </Link>
            <button type="button" onClick={() => onAdd(p)} data-testid={`concern-add-${p.slug}`}
                disabled={p.purchasable === false || p.coming_soon}
                className="inline-flex items-center justify-center gap-1.5 rounded-full px-2 py-2.5 text-[10px] whitespace-nowrap
                    uppercase tracking-[0.12em] text-[#141414] hover:brightness-95 disabled:opacity-40
                    disabled:cursor-not-allowed transition-[filter]"
                style={{ backgroundColor: accent }}>
                <ShoppingBag className="w-3.5 h-3.5" /> {p.coming_soon ? "Soon" : cartLabel}
            </button>
        </div>
    </div>
    );
};

export const ShopByConcern = ({ props: p = {} }) => {
    const accent = p.accent_hex || "#A4E22B";
    const bg = p.bg_hex || "#141414";
    const cardBg = p.card_hex || "#FAFAF8";
    const tabs = (p.tabs || []).filter((t) => t.visible !== false && (t.label || t.concern));
    const [active, setActive] = useState(0);
    const { addItem } = useCart();
    const { data: products = [] } = useQuery({
        queryKey: ["products"], queryFn: fetchProducts, staleTime: 15000,
    });

    const tab = tabs[Math.min(active, Math.max(tabs.length - 1, 0))] || {};
    const list = useMemo(() => {
        const want = (tab.concern || tab.label || "").trim().toLowerCase();
        if (!want) return [];
        const rows = products.filter((pr) => (pr.shop_type || "product") === "product"
            && (pr.concerns || []).some((c) => (c || "").trim().toLowerCase() === want));
        const limit = Number(tab.limit) > 0 ? Number(tab.limit) : 8;
        return rows.slice(0, limit);
    }, [products, tab]);

    if (!tabs.length) return null;

    const add = (product) => {
        addItem(product, 1);
        toast.success(`${product.name} added to cart`);
    };

    return (
        <section className="mx-auto max-w-7xl px-4 md:px-12 py-10 md:py-16" data-testid="shop-by-concern">
            <div className="rounded-[28px] md:rounded-[36px] px-5 md:px-12 pt-10 md:pt-16 pb-8 md:pb-12"
                style={{ backgroundColor: bg }}>
                {p.eyebrow && (
                    <div className="mono text-[10px] tracking-[0.28em] text-center mb-4" style={{ color: accent }}>
                        {p.eyebrow.toUpperCase()}
                    </div>
                )}
                <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight text-center"
                    style={{ color: "#FFFFFF" }}>
                    {p.heading_prefix || "Shop by"}{" "}
                    <span style={{ color: accent }}>{p.heading_accent || "Concern"}</span>
                </h2>
                {p.body && (
                    <p className="mt-4 text-sm md:text-base text-white/55 text-center max-w-2xl mx-auto leading-relaxed">
                        {p.body}
                    </p>
                )}

                <div className="mt-9 md:mt-12 flex items-end gap-1 overflow-x-auto no-scrollbar -mb-px px-1"
                    role="tablist" data-testid="concern-tabs">
                    {tabs.map((t, i) => {
                        const on = i === Math.min(active, tabs.length - 1);
                        return (
                            <button key={`${t.label}-${i}`} type="button" role="tab" aria-selected={on}
                                onClick={() => setActive(i)} data-testid={`concern-tab-${i}`}
                                className={`relative shrink-0 rounded-t-2xl px-5 md:px-7 pt-3.5 pb-4 mono text-[10px] md:text-[11px]
                                    tracking-[0.18em] transition-colors duration-200 ${on
                                    ? "text-[#141414]" : "text-white/45 hover:text-white/80"}`}
                                style={on ? { backgroundColor: cardBg } : undefined}>
                                {(t.label || t.concern).toUpperCase()}
                                {on && (
                                    <span aria-hidden className="absolute left-5 right-5 md:left-7 md:right-7 bottom-1.5 h-[3px] rounded-full"
                                        style={{ backgroundColor: accent }} />
                                )}
                            </button>
                        );
                    })}
                </div>

                <div className="rounded-[22px] md:rounded-[28px] rounded-tl-none p-5 md:p-8"
                    style={{ backgroundColor: cardBg }} data-testid="concern-panel">
                    {list.length ? (
                        <div className={`grid gap-4 md:gap-5 ${COLS[p.columns] || COLS[4]}`}>
                            {list.map((pr) => (
                                <ConcernCard key={pr.slug} p={pr} accent={accent} onAdd={add}
                                    detailsLabel={p.details_label || "Details"}
                                    cartLabel={p.cart_label || "Add to Cart"} />
                            ))}
                        </div>
                    ) : (
                        <p className="py-10 text-center text-sm text-black/45" data-testid="concern-empty">
                            {p.empty_message || "Nothing tagged to this concern yet — pick another tab."}
                        </p>
                    )}
                </div>
            </div>
        </section>
    );
};

export default ShopByConcern;
